import { Checkbox, Stack, Text, TextInput } from '@mantine/core';
import { RIDES_VERIFICATION_TYPE, type RidesDocumentCheckResult, type RidesVerificationView } from '@actschurch/ccms-sdk';
import type { GuardianDraft } from '@/store/ridesWizard';
import { IdentityDocumentForm } from './IdentityDocumentForm';
import { isSubmitted } from '@/lib/me';
import { useCopy, guardianRelationshipList } from '@/lib/settings';
import { PickerCard, PickerGroup } from '@/components/ui/PickerCard';
import { PhoneInput } from '@/components/ui/PhoneInput';
import '@/styles/flows.css';

interface Props {
  guardian: GuardianDraft;
  onGuardianChange: (g: GuardianDraft) => void;
  consent: boolean;
  onConsentChange: (v: boolean) => void;
  guardianVerification?: RidesVerificationView | null;
  onGuardianDocumentResult: (r: RidesDocumentCheckResult) => void;
  providerName?: string | null;
}

/** Name, relationship and a cellphone number we can call (9 or more digits). */
export function guardianDetailsComplete(g: GuardianDraft): boolean {
  return g.name.trim().length > 1 && g.relationship.trim().length > 0 && g.mobile.replace(/\D/g, '').length >= 9;
}

export function guardianComplete(g: GuardianDraft, consent: boolean): boolean {
  return guardianDetailsComplete(g) && consent;
}

/** The parent or guardian's details: full name, relationship (big choices) and cellphone number. */
export function GuardianDetailsFields({ guardian, onChange, showErrors, disabled }: { guardian: GuardianDraft; onChange: (g: GuardianDraft) => void; showErrors?: boolean; disabled?: boolean }) {
  const { settings } = useCopy();
  const set = (patch: Partial<GuardianDraft>) => onChange({ ...guardian, ...patch });
  const relationships = guardianRelationshipList(settings);
  const options = guardian.relationship && !relationships.includes(guardian.relationship) ? [...relationships, guardian.relationship] : relationships;
  return (
    <Stack gap="md">
      <TextInput
        label="Full name"
        autoComplete="name"
        value={guardian.name}
        onChange={(e) => set({ name: e.currentTarget.value })}
        required
        disabled={disabled}
        error={showErrors && guardian.name.trim().length < 2 ? 'Please enter the full name.' : undefined}
      />
      <PickerGroup label="Relationship to the child">
        {options.map((r) => (
          <PickerCard key={r} selected={guardian.relationship === r} onSelect={() => set({ relationship: r })} title={r} disabled={disabled} />
        ))}
      </PickerGroup>
      {showErrors && !guardian.relationship && <Text c="var(--rides-bad)" fw={600} size="sm" role="alert">Please choose the relationship.</Text>}
      <PhoneInput
        value={guardian.mobile}
        onChange={(v) => set({ mobile: v })}
        description="Campus security may call this number on the day."
        required
        disabled={disabled}
        error={showErrors && guardian.mobile.replace(/\D/g, '').length < 9 ? 'Please enter a cellphone number.' : undefined}
      />
    </Stack>
  );
}

/** The guardian consent (copy key consent.guardian_text, legal wording shown in full) and its "I agree" box. */
export function GuardianConsent({ checked, onChange, error, disabled }: { checked: boolean; onChange: (v: boolean) => void; error?: boolean; disabled?: boolean }) {
  const { t } = useCopy();
  return (
    <Stack gap="sm">
      <div className="rides-legalbox">{t('consent.guardian_text')}</div>
      <Checkbox
        checked={checked}
        onChange={(e) => onChange(e.currentTarget.checked)}
        disabled={disabled}
        styles={{ body: { alignItems: 'flex-start' }, label: { paddingInlineStart: 10, fontSize: 16 } }}
        label="I am the child's parent or legal guardian and I agree."
        error={error ? 'Please tick the box to agree.' : undefined}
      />
    </Stack>
  );
}

/**
 * Everything for an under-18 booking in one block (details, consent and the guardian's ID check).
 * Kept for older screens; the booking step now asks these one screen at a time.
 */
export function GuardianBox({ guardian, onGuardianChange, consent, onConsentChange, guardianVerification, onGuardianDocumentResult }: Props) {
  const docDone = isSubmitted(guardianVerification);
  return (
    <Stack gap="lg">
      <GuardianDetailsFields guardian={guardian} onChange={onGuardianChange} />
      <GuardianConsent checked={consent} onChange={onConsentChange} />
      {!docDone && (
        <IdentityDocumentForm typeId={RIDES_VERIFICATION_TYPE.GuardianId} onResult={onGuardianDocumentResult} whose="the guardian's" submitLabel="Check the ID" />
      )}
    </Stack>
  );
}
